// src/views/Seguridad/Roles/CrearRoles.jsx
import React from "react";
import { FaArrowLeft } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

const FIXED_PERMISSION_ID = 117;
/* ===== helper: traer todas las opciones paginadas ===== */
async function fetchAllOptions(buildUrl, mapFn, { signal } = {}) {
  let page = 0;
  const acc = [];
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const url = buildUrl(page);
    const data = await apiFetchJson(url, { signal });
    const items = data?.items ?? data?.content ?? [];
    acc.push(...items.map(mapFn));
    const hasMore = Boolean(data?.hasMore ?? items.length >= (data?.size ?? 200));
    if (!hasMore) break;
    page = (data?.page ?? page) + 1;
  }
  // dedup por id
  const seen = new Set();
  return acc.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
}

/* ======================= TAB: PERMISOS (Create) ======================= */
const TabPermisos = React.forwardRef(function TabPermisos({ value = [], onChange }, ref) {
  const [options, setOptions] = React.useState([]);
  const [loadingOpts, setLoadingOpts] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const boxRef = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingOpts(true);
        const all = await fetchAllOptions(
          (page) => `${apiRoutes.permisos.list}?page=${page}&size=200`,
          (it) => {
            const id = Number(it.id ?? it.perId ?? it.PER_SQ_COD);
            const modulo = it.moduloNombre ?? it.modulo ?? it.menuNombre ?? "";
            const submod = it.submoduloNombre ?? it.subModuloNombre ?? it.submodulo ?? "";
            const accion = (it.accion ?? it.permiso ?? "").toString().toUpperCase();
            return { id, modulo, submodulo: submod, accion, label: `${modulo} – ${submod} – ${accion}` };
          }
        );
        if (!cancelled) {
          setOptions(all);
        
          

          onChange?.((prev) => {
            const arr = Array.isArray(prev) ? prev : [];
            return arr.includes(FIXED_PERMISSION_ID)
              ? arr
              : [...arr, FIXED_PERMISSION_ID];
          });
        }
      } finally {
        if (!cancelled) setLoadingOpts(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => (o.label || "").toLowerCase().includes(needle));
  }, [options, q]);

  const selectedIds = Array.isArray(value) ? value : [];

  const toggle = (id) => {
    if (id === FIXED_PERMISSION_ID) return; // ❌ no permitir quitarlo

    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onChange?.(next);
  };

  const marcarTodas = () => {
    const ids = filtered.map((f) => f.id);
    const set = new Set([...selectedIds, ...ids]);
    onChange?.(Array.from(set));
  };

  const desmarcarTodas = () => {
    const ids = new Set(filtered.map((f) => f.id));
    onChange?.(selectedIds.filter((id) => !ids.has(id)));
  };

  React.useImperativeHandle(ref, () => ({
    getValue: () => ({ permisos: selectedIds })
  }));

  const label =
    loadingOpts
      ? "Cargando permisos…"
      : selectedIds.length === 0
      ? "Seleccionar"
      : selectedIds.length === 1
      ? "1 seleccionado"
      : `${selectedIds.length} seleccionados`;

  return (
    <div className="rounded-md border border-gray-200 bg-white px-4 py-4">
      <div className="grid grid-cols-12 gap-4 max-w-3xl">
        <div className="col-span-12">
          <label className="label-strong">Permisos:</label>
          <div ref={boxRef} className="relative mt-1">
            <button
              type="button"
              className={`input w-full flex items-center text-left pl-3 pr-2 ${selectedIds.length ? "border-green-600" : ""}`}
              onClick={() => setOpen((v) => !v)}
              disabled={loadingOpts}
              title="Módulo – Submódulo – ACCIÓN"
            >
              <span className="truncate">{label}</span>
              <span className="ml-auto">▾</span>
            </button>

            {open && (
              <div className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl">
                <div className="sticky top-0 z-10 bg-white border-b p-2 flex flex-wrap items-center gap-2">
                  <input
                    placeholder="Filtrar permiso (Módulo, Submódulo o Acción)"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="flex-1 border rounded px-2 py-1"
                  />
                  <span className="text-sm text-gray-600">Opciones:</span>
                  <button type="button" onClick={marcarTodas} className="text-green-700 text-sm hover:underline">
                    ✓ Marcar todas
                  </button>
                  <button type="button" onClick={desmarcarTodas} className="text-green-700 text-sm hover:underline">
                    ✗ Desmarcar todas
                  </button>
                </div>

                <div className="max-h-80 overflow-auto">
                  {filtered.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">Sin coincidencias</div>
                  )}
                  {filtered.map((op) => {
                    const checked = selectedIds.includes(op.id);
                    return (
                      <label
                        key={op.id}
                        className="flex items-start gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        title={op.label}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(op.id)}
                          className="mt-1"
                          disabled={op.id === FIXED_PERMISSION_ID}   // 🔒 bloqueado
                        />
                        <span className="whitespace-normal leading-snug">
                          {op.modulo} – {op.submodulo} – {op.accion}
                        </span>
                      </label>
                    );
                  })}
                </div>

                <div className="p-2 border-t flex items-center justify-end gap-2 bg-white">
                  <button type="button" className="btn-fil" onClick={() => setOpen(false)}>
                    Cerrar
                  </button>
                </div>
              </div>
            )}
          </div>
          {selectedIds.length === 0 && (
            <div className="mt-2 text-sm text-red-600">Seleccioná al menos un permiso.</div>
          )}
        </div>
      </div>
    </div>
  );
});

/* ======================= TAB: DATOS (Create) ======================= */
const TabDatos = React.forwardRef(function TabDatos({ nombre, tipo, onChange }, ref) {
  React.useImperativeHandle(ref, () => ({
    getValue: () => ({ nombre, tipo })
  }));
  return (
    <div className="space-y-6">
      <div className="rounded-md border border-gray-200 bg-white px-4 py-3">
        <div className="form-row gap-2 items-center">
          <div className="form-label">Nombre*</div>
          <div className="flex-1">
            <input
              className="input w-full"
              placeholder="Ej: ROL_ADMIN_FINANZAS"
              value={nombre}
              maxLength={50}
              onChange={(e) => onChange?.({ nombre: e.target.value })}
            />
          </div>
        </div>

        <div className="mt-3 form-row gap-2 items-center">
          <div className="form-label">Tipo</div>
          <div className="flex-1">
            <select
              className="input w-full"
              value={tipo}
              onChange={(e) => onChange?.({ tipo: e.target.value })}
            >
              <option value="CLIENTE">Cliente</option>
              <option value="STAFF">Staff</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
});

/* ======================= CONTENEDOR (Create) ======================= */
export default function CrearRoles({
  onVolver = () => {},
  onSaved = () => {},
}) {
  const [tab, setTab] = React.useState("datos"); // 'datos' | 'permisos'

  // estado levantado al padre
  const [nombre, setNombre] = React.useState("");
  const [tipo, setTipo] = React.useState("CLIENTE");
  const [permIds, setPermIds] = React.useState([]);

  const datosRef = React.useRef(null);
  const permsRef = React.useRef(null);

  // ambas pestañas quedan montadas; solo se ocultan
  const canSave = nombre.trim() !== "" && permIds.length > 0;
  const savingRef = React.useRef(false);

  // 👉 evita dobles llamados al cerrar el overlay
const closeOnceRef = React.useRef(false);
const handleCloseAndReturn = (payload) => {
  if (closeOnceRef.current) return;
  closeOnceRef.current = true;
  try { onSaved(payload); } finally { onVolver(); }
};
  // ====== Overlay helpers ======
const openOverlay = (detail) => {
  // ahora OverlayHost entiende {mode,title,message,onClose}
  if (window.overlay?.open) window.overlay.open(detail || {});
  else window.dispatchEvent(new CustomEvent("overlay:open", { detail }));
};

  const onGuardar = async () => {
  if (savingRef.current || !canSave) return;
  savingRef.current = true;

  openOverlay({
    mode: "loading",
    title: "Creando rol…",
    message: "Por favor, espere sin cerrar ni recargar la página.",
  });

  try {
    const body = {
      nombre: nombre.trim(),
      tipo: (tipo || "CLIENTE").toUpperCase(),
      //permisos: permIds.map(Number),
      permisos: Array.from(new Set([...permIds.map(Number), FIXED_PERMISSION_ID])),

    };

    const resp = await apiFetchJson(apiRoutes.roles.create, {
      method: "POST",
      headers: { "Content-Type": "application/json; charset=UTF-8" },
      body: JSON.stringify(body),
    });

    openOverlay({
      mode: "success",
      title: "Operación realizada",
      message: `El rol “${resp?.nombre ?? body.nombre}” fue creado correctamente.`,
      onClose: () => handleCloseAndReturn(resp), // ✅ vuelve al padre y recarga
    });
  } catch (e) {
    const msg =
      e?.message || (typeof e?.body === "string" ? e.body : null) || "No se pudo crear el rol.";
    openOverlay({
      mode: "error",
      title: "Error",
      message: msg,
      onClose: () => handleCloseAndReturn(null),   // ✅ también vuelve en error
    });
  } finally {
    savingRef.current = false;
  }
};

  return (
    <div className="page max-w-5xl px-4">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <button type="button" className="btn-fil" onClick={onVolver}>
          <FaArrowLeft className="inline mr-2" /> Volver
        </button>
        <h2 className="page-title m-0">Crear Rol</h2>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex gap-2">
        <button
          type="button"
          className={`px-3 py-2 rounded-md border ${
            tab === "datos" ? "bg-emerald-100 border-emerald-300" : "bg-white border-gray-200"
          }`}
          onClick={() => setTab("datos")}
        >
          Datos del rol
        </button>
        <button
          type="button"
          className={`px-3 py-2 rounded-md border ${
            tab === "permisos" ? "bg-emerald-100 border-emerald-300" : "bg-white border-gray-200"
          }`}
          onClick={() => setTab("permisos")}
        >
          Permisos
        </button>
      </div>

      {/* Ambas pestañas montadas; se ocultan con 'hidden' */}
      <div hidden={tab !== "datos"}>
        <TabDatos
          ref={datosRef}
          nombre={nombre}
          tipo={tipo}
          onChange={({ nombre: n, tipo: t }) => {
            if (n !== undefined) setNombre(n);
            if (t !== undefined) setTipo(t);
          }}
        />
      </div>

      <div hidden={tab !== "permisos"}>
        <TabPermisos
          ref={permsRef}
          value={permIds}
          onChange={setPermIds}
        />
      </div>

      {/* Footer */}
      <div className="mt-6 text-right">
        <button
          type="button"
          className="btn-fil disabled:opacity-60 disabled:cursor-not-allowed"
          onClick={onGuardar}
          disabled={!canSave || savingRef.current}
          title={
            !nombre.trim()
              ? "Ingresá el nombre del rol"
              : permIds.length === 0
              ? "Seleccioná al menos un permiso"
              : "Crear rol"
          }
        >
          Guardar
        </button>
      </div>
    </div>
  );
}
